import { useEffect } from 'react';

/**
 * Tracks the actual visible viewport height (excluding the iOS keyboard
 * when it's open) and exposes it as a CSS custom property on the html
 * element: `--viewport-h: <px>px`.
 *
 * Why this exists: on iOS Safari, when an input is focused and the
 * keyboard opens, the document doesn't automatically shrink. iOS instead
 * scrolls the page up to keep the focused input above the keyboard. For
 * an app shell with a sticky header that's the wrong behavior — the
 * header gets pushed off-screen.
 *
 * Fix: use `window.visualViewport` to track the actual visible area, set
 * the html height to it via this custom property, and `overflow: hidden`
 * on body to prevent the document from scrolling in the first place. The
 * chat form ends up at the bottom of the (now shorter) body, just above
 * the keyboard; the header stays at the top; the transcript shrinks in
 * between.
 */
export function useViewportHeight(): void {
  useEffect(() => {
    const update = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--viewport-h', `${h}px`);
      // Defensive: iOS Safari may have already auto-scrolled the document
      // up to bring the focused input above the keyboard. Body is
      // position:fixed, so scrolling shouldn't matter for layout — but
      // resetting scroll to 0,0 keeps things consistent across refresh,
      // orientation change, and the focus/blur cycle.
      window.scrollTo(0, 0);
    };

    update();

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
      return () => {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      };
    }

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
}
